/**
 * ConfirmEmailChangeScreen — handles the deep-link / web route that arrives
 * after the user clicks the email-change confirmation link in their inbox.
 *
 * Route: (auth)/confirm-email-change/[token]
 * Web URL: https://warcarp.com/confirm-email-change/<token>
 *
 * States:
 *   loading  — POST in flight; spinner
 *   success  — fires toast then redirects to /(app); the app layout redirects
 *              to login if the session is unauthenticated, so both cases are
 *              handled for free.
 *   error    — token invalid/expired/race-loss; back-to-sign-in link
 */

import React, { useEffect, useState } from 'react'
import { View, Text, ActivityIndicator } from 'react-native'
import { Link, useLocalSearchParams, useRouter } from 'expo-router'
import { useToast } from '../../../components/ui/Toast'
import { apiPost, HttpError } from '../../../lib/api'
import type {
  ConfirmEmailChangeRequest,
  ConfirmEmailChangeResponse,
} from '@warcarp/api-types'

type ScreenState = 'loading' | 'success' | 'error'

export default function ConfirmEmailChangeScreen() {
  const { token } = useLocalSearchParams<{ token: string }>()
  const router = useRouter()
  const { showToast } = useToast()

  const [state, setState] = useState<ScreenState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setErrorMessage(null)
      setState('error')
      return
    }

    let cancelled = false

    apiPost<ConfirmEmailChangeResponse>(
      '/api/v1/auth/confirm-email-change',
      { token } satisfies ConfirmEmailChangeRequest,
    )
      .then(() => {
        if (cancelled) return
        showToast('Email successfully changed', 'success')
        router.replace('/(app)')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (err instanceof HttpError && err.status === 400) {
          // EMAIL_TAKEN race — another account grabbed the address first.
          setErrorMessage(
            'That email address has already been taken by another account. Please request a new change.',
          )
        } else {
          setErrorMessage(null)
        }
        setState('error')
      })

    return () => {
      cancelled = true
    }
  }, [token])

  if (state === 'loading') {
    return (
      <View className="flex-1 bg-canvas justify-center items-center gap-4">
        <ActivityIndicator size="large" />
        <Text className="font-typed text-sm text-ink-soft">Confirming your email change…</Text>
      </View>
    )
  }

  // error state (success redirects immediately via router.replace — no render needed)
  return (
    <View className="flex-1 bg-canvas justify-center px-6 gap-8">
      <View className="gap-3">
        <Text className="text-2xl font-stencil text-ink uppercase">Link not valid</Text>
        <Text className="font-typed text-sm text-ink-soft">
          {errorMessage ??
            "We couldn't confirm this change. The link may have expired or been used."}
        </Text>
      </View>

      <Link
        href="/(auth)/login"
        className="text-blood font-semibold text-base text-center font-typed"
      >
        Back to Sign In
      </Link>
    </View>
  )
}
